import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Star, Plus, X } from "lucide-react";
import { DEFAULT_CATEGORIES } from "../../lib/constants";

export default function Vendors() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState(""); // ""|yes|no
  const [typeFilter, setTypeFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loadError, setLoadError] = useState("");
  const [drafts, setDrafts] = useState({});
  const [rowSaveState, setRowSaveState] = useState({});

  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (verifiedFilter) p.set("verified", verifiedFilter);
      if (typeFilter) p.set("vendor_type", typeFilter);
      const r = await api.get(`/admin/vendors${p.toString() ? "?" + p.toString() : ""}`);
      setVendors(r.data.vendors);
      setLoadError("");
      return r.data.vendors;
    } catch {
      setLoadError("Could not refresh the vendor list. Your change may have saved; refresh and check before trying again.");
      return null;
    }
  };

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCategories(r.data.categories || DEFAULT_CATEGORIES));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, verifiedFilter, typeFilter]);

  const verify = async (id, e) => {
    e?.stopPropagation();
    try {
      const r = await api.post(`/admin/vendors/${id}/approve`);
      const refreshed = await load();
      const confirmed = r.data.vendor?.is_approved ||
        refreshed?.some((vendor) => vendor.id === id && vendor.is_approved);
      if (!confirmed) {
        toast.error("The server did not confirm this vendor as verified. Refresh the page before retrying.");
        return;
      }
      toast.success("Verified");
    } catch {
      toast.error("Could not verify this vendor. Please retry and check the vendor status.");
    }
  };
  const editCell = (id, field, value) => {
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), [field]: value },
    }));
  };
  const saveCell = async (vendor, field) => {
    const raw = drafts[vendor.id]?.[field];
    if (raw === undefined) return;
    const value = field === "instagram"
      ? raw.trim().replace(/^@/, "")
      : raw.trim();
    const savedValue = String(vendor[field] || "").trim();
    if (value === savedValue) {
      setDrafts((current) => {
        const row = { ...(current[vendor.id] || {}) };
        delete row[field];
        const next = { ...current };
        if (Object.keys(row).length) next[vendor.id] = row;
        else delete next[vendor.id];
        return next;
      });
      return;
    }
    setRowSaveState((current) => ({ ...current, [vendor.id]: "saving" }));
    try {
      await api.patch(`/admin/vendors/${vendor.id}`, { [field]: value });
      const refreshed = await load();
      if (!refreshed) throw new Error("Refresh failed");
      setDrafts((current) => {
        const row = { ...(current[vendor.id] || {}) };
        delete row[field];
        const next = { ...current };
        if (Object.keys(row).length) next[vendor.id] = row;
        else delete next[vendor.id];
        return next;
      });
      setRowSaveState((current) => ({ ...current, [vendor.id]: "saved" }));
    } catch {
      setRowSaveState((current) => ({ ...current, [vendor.id]: "error" }));
      toast.error(`Could not save ${field.replaceAll("_", " ")}. Your edit is still in the cell; try leaving it again.`);
    }
  };
  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete vendor?")) return;
    await api.delete(`/admin/vendors/${id}`);
    toast.success("Deleted");
    load();
  };

  const counts = useMemo(() => {
    let v = 0, u = 0;
    vendors.forEach((x) => x.is_approved ? v++ : u++);
    return { verified: v, unverified: u, total: vendors.length };
  }, [vendors]);

  return (
    <div className="p-6 lg:p-10" data-testid="vendors-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="btw-overline mb-1">Vendor directory</p>
          <h1 className="text-3xl font-bold">Vendors</h1>
          <p className="text-xs text-[#5c5852] mt-1" data-testid="vendor-counts">
            {counts.total} shown · {counts.verified} verified · {counts.unverified} unverified
          </p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <input
            placeholder="Search by name…"
            data-testid="vendors-search"
            className="px-3 py-2 border border-[#dcd3c6] bg-white text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            data-testid="vendors-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-[#dcd3c6] bg-white text-sm"
          >
            <option value="">All types</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="vendors-verified-filter"
            value={verifiedFilter}
            onChange={(e) => setVerifiedFilter(e.target.value)}
            className="px-3 py-2 border border-[#dcd3c6] bg-white text-sm"
          >
            <option value="">All vendors</option>
            <option value="yes">Verified only</option>
            <option value="no">Unverified only</option>
          </select>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-vendor-button"
            className="btw-btn text-xs px-4 py-2 inline-flex items-center gap-1"
          >
            <Plus size={14} /> New vendor
          </button>
        </div>
      </div>

      {loadError && <p role="alert" className="mb-4 text-sm text-[#b35c5c]">{loadError}</p>}

      <div className="bg-white border border-[#dcd3c6] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-[#5c5852] border-b border-[#dcd3c6]">
              <th className="text-left px-4 py-3 w-8" title="Active partner">★</th>
              <th className="text-left px-4 py-3" title="Verified">Verified</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Website</th>
              <th className="text-left px-4 py-3">Instagram</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Events</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => (
              <tr
                key={v.id}
                onClick={() => navigate(`/admin/vendors/${v.id}`)}
                className="border-b border-[#dcd3c6]/50 hover:bg-[#e2d3c0]/20 cursor-pointer"
                data-testid={`vendor-row-${v.id}`}
              >
                <td className="px-4 py-3">
                  {v.is_partner && (
                    <Star size={14} className="text-[#876a4f] fill-[#c19872]" data-testid={`partner-star-${v.id}`} />
                  )}
                </td>
                <td className="px-4 py-3">
                  {v.is_approved ? (
                    <span title="Verified" className="inline-block w-2 h-2 rounded-full bg-[#768c6a]" data-testid={`verified-${v.id}`} />
                  ) : (
                    <span title="Unverified" className="inline-block w-2 h-2 rounded-full border border-[#dcd3c6]" data-testid={`unverified-${v.id}`} />
                  )}
                </td>
                {["name", "type", "website", "instagram", "email"].map((field) => (
                  <td key={field} className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      aria-label={`${field.replaceAll("_", " ")} for ${v.name}`}
                      data-testid={`vendor-${field}-${v.id}`}
                      value={drafts[v.id]?.[field] ?? v[field] ?? ""}
                      placeholder={field === "name" ? "Business name" : "—"}
                      onChange={(e) => editCell(v.id, field, e.target.value)}
                      onBlur={(e) => {
                        if (e.currentTarget.dataset.cancelled === "true") {
                          delete e.currentTarget.dataset.cancelled;
                          return;
                        }
                        saveCell(v, field);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") {
                          e.currentTarget.dataset.cancelled = "true";
                          setDrafts((current) => {
                            const row = { ...(current[v.id] || {}) };
                            delete row[field];
                            const next = { ...current };
                            if (Object.keys(row).length) next[v.id] = row;
                            else delete next[v.id];
                            return next;
                          });
                          e.currentTarget.blur();
                        }
                      }}
                      className="w-full min-w-[100px] bg-transparent px-2 py-1 border border-transparent hover:border-[#dcd3c6] focus:border-[#c19872] focus:bg-white outline-none"
                    />
                  </td>
                ))}
                <td className="px-4 py-3 text-[#5c5852]">{v.event_count}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="text-[10px] text-[#5c5852] min-w-[42px]" aria-live="polite">
                      {rowSaveState[v.id] === "saving" ? "Saving…" : rowSaveState[v.id] === "saved" ? "Saved" : rowSaveState[v.id] === "error" ? "Retry" : ""}
                    </span>
                    {!v.is_approved && (
                      <button onClick={(e) => verify(v.id, e)} data-testid={`verify-vendor-${v.id}`} className="text-xs px-2 py-1 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
                        Verify
                      </button>
                    )}
                    <button onClick={(e) => remove(v.id, e)} data-testid={`del-vendor-${v.id}`} className="text-xs text-[#b35c5c] hover:underline">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {vendors.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center italic text-[#5c5852]">No vendors match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <NewVendorModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={(v) => { setShowCreate(false); navigate(`/admin/vendors/${v.id}`); }}
        />
      )}
    </div>
  );
}

const NewVendorModal = ({ categories, onClose, onCreated }) => {
  const [f, setF] = useState({
    name: "", type: "", website: "", instagram: "", email: "", contact_name: "", is_partner: false,
  });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/vendors", f);
      toast.success("Vendor created");
      onCreated(r.data);
    } catch {
      toast.error("Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center px-4" data-testid="new-vendor-modal">
      <form onSubmit={create} className="bg-[#f5f3ed] border border-[#dcd3c6] max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">New vendor</h2>
          <button type="button" onClick={onClose} className="text-[#5c5852] hover:text-[#b35c5c]" data-testid="close-modal">
            <X size={18} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name *" testid="modal-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
          <div>
            <label className="btw-overline block mb-1">Type / category</label>
            <select
              data-testid="modal-type"
              value={f.type}
              onChange={(e) => setF({ ...f, type: e.target.value })}
              className="w-full bg-transparent border-b border-[#dcd3c6] focus:border-[#c19872] outline-none py-2 text-sm"
            >
              <option value="">— Choose —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Field label="Website" testid="modal-website" value={f.website} onChange={(v) => setF({ ...f, website: v })} />
          <Field label="Instagram (no @)" testid="modal-instagram" value={f.instagram} onChange={(v) => setF({ ...f, instagram: v.replace(/^@/, "") })} />
          <Field label="Email" testid="modal-email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
          <Field label="Contact name" testid="modal-contact" value={f.contact_name} onChange={(v) => setF({ ...f, contact_name: v })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#5c5852] mt-4 cursor-pointer">
          <input
            type="checkbox"
            data-testid="modal-partner"
            checked={f.is_partner}
            onChange={(e) => setF({ ...f, is_partner: e.target.checked })}
            className="h-4 w-4 accent-[#c19872]"
          />
          <Star size={14} className={f.is_partner ? "text-[#876a4f] fill-[#c19872]" : "text-[#5c5852]"} />
          Active partner with DWH / DWT
        </label>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="text-sm text-[#5c5852] hover:underline">Cancel</button>
          <button type="submit" disabled={busy} data-testid="modal-create" className="btw-btn text-xs px-4 py-2">
            {busy ? "Creating…" : "Create vendor"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="btw-overline block mb-1">{label}</label>
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-[#dcd3c6] focus:border-[#c19872] outline-none py-2 text-sm"
    />
  </div>
);
